import React, { useState, useEffect } from 'react';
import { X, QrCode, Wallet, Clock, CheckCircle2, Copy, Upload, User, Image, AlertCircle, ChevronDown, ChevronUp, FileText, MessageCircle, Zap, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

// No Refund Policy Content
const NO_REFUND_POLICY = `No Refund Policy

1. All Sales Are Final
All purchases of Bonbot, including but not limited to software access, subscriptions, and related services, are final and non-refundable. Once access to Bonbot is granted, no refunds, chargebacks, or reversals will be issued under any circumstances.

2. Signal-Only Service (No Automated Trading)
Bonbot is a signal-generating tool only. It does not execute trades, place orders, manage accounts, or trade on behalf of users. All trading decisions, trade execution, position sizing, and risk management remain entirely the responsibility of the user.

3. Trading Risk Disclosure
By purchasing and using Bonbot, you acknowledge and agree that:
• Trading financial markets involves significant risk
• Signals provided by Bonbot are not guaranteed to be accurate or profitable
• Market conditions can change rapidly and without notice
• Past performance does not guarantee future results

Refunds will not be issued due to:
• Trading losses or missed opportunities
• Dissatisfaction with signal accuracy or performance
• Market volatility or unexpected market events
• User execution errors, delays, or misinterpretation of signals

4. No Financial Advice
All signals, alerts, and information provided by Bonbot are for educational and informational purposes only and do not constitute financial, investment, or trading advice. Users should conduct their own research and consult a qualified financial professional if needed before trading.

5. Technical & Service Limitations
While we aim to provide a reliable service, Bonbot is not responsible for:
• Internet or connectivity issues
• Platform, broker, or exchange limitations
• Third-party service outages or failures
• Delayed, missed, or undelivered signals due to factors beyond our control
These circumstances do not qualify for a refund.

6. Chargebacks & Policy Enforcement
Any attempt to initiate a chargeback, payment dispute, or unauthorized reversal will result in immediate termination of access to Bonbot without notice and without refund.

7. Acceptance of Policy
By purchasing, accessing, or using Bonbot, you confirm that you have read, understood, and agreed to this No Refund Policy in full.`;

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  // On production custom domain, always use tradepayflow.emergent.host
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  // Otherwise use env variable or fallback to origin
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

// Plan configurations
const PLANS = {
  essential: {
    name: 'Essential Plan',
    price: '$20',
    duration: '5 Days',
    icon: Zap,
    color: 'blue',
    features: ['Signal Generation', 'Trade Tracking', 'Basic Support'],
    offerCodeEligible: false
  },
  premium: {
    name: 'Premium Plan',
    price: '$59.99',
    duration: '1 Month',
    icon: Crown,
    color: 'purple',
    features: ['Signal Generation', 'Trade Tracking', 'Priority Support', 'Offer Codes Eligible', 'Extended Access'],
    offerCodeEligible: true
  }
};

const WHATSAPP_LINK = "https://wa.me/message/3PBT5TZW3SARB1";

// Coaching package configurations
const COACHING_PACKAGES = {
  '30min': {
    name: '30-Minute Session',
    price: '$55',
    priceNum: 55,
    duration: '30 minutes',
    description: 'Quick start guide',
    features: [
      'How to use the trading tool effectively',
      'Understanding market structure',
      'How the market moves',
      'What to look for during market movements'
    ]
  },
  '1hr': {
    name: '1-Hour Session',
    price: '$100',
    priceNum: 100,
    duration: '60 minutes',
    description: 'Comprehensive coaching',
    features: [
      'Market structure & how the market moves',
      'Strategy to take advantage of market reactions',
      'Understanding key zones',
      'Recognizing manipulation vs natural behavior',
      'Q&A related to the coaching session'
    ]
  },
  'custom': {
    name: 'Custom Package',
    price: 'Contact',
    priceNum: 0,
    duration: 'Flexible',
    description: 'Tailored to your needs',
    features: [
      'Customized content based on your requirements',
      'Flexible duration',
      'Contact for pricing'
    ]
  }
};

const PaymentModal = ({ isOpen, onClose, onPaymentSubmitted, userEmail, isTrialExpired = false, forcePayment = false, isNewSignup = false }) => {
  const { t } = useLanguage();
  const QR_CODE_URL = "https://customer-assets.emergentagent.com/job_b4c8a9d0-7ea2-48c0-817a-eed35237bb3f/artifacts/0i16gwr9_qrccc.jpeg";
  const PAYMENT_NETWORK = "BNB Smart Chain (BEP-20)";
  const PAYMENT_TOKEN = "USDT";
  const WALLET_ADDRESS = "0x8c492198161A497276475B57880E1F2FD20e192D";

  // Mode selection: 'subscription' or 'coaching'
  const [paymentMode, setPaymentMode] = useState('subscription');

  // Plan selection state
  const [selectedPlan, setSelectedPlan] = useState('premium');
  
  // Coaching selection state
  const [selectedCoachingPkg, setSelectedCoachingPkg] = useState('1hr');
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState(null); // 'crypto'
  
  // Payment proof upload state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [payerName, setPayerName] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [proofSubmitted, setProofSubmitted] = useState(false);
  
  // No Refund Policy state
  const [showRefundPolicy, setShowRefundPolicy] = useState(false);
  const [acceptedRefundPolicy, setAcceptedRefundPolicy] = useState(false);

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  
  // Offer code state
  const [offerCode, setOfferCode] = useState('');
  
  // Billing address state
  const [billingStreet, setBillingStreet] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [sameAsMailing, setSameAsMailing] = useState(false);
  const [mailingAddress, setMailingAddress] = useState(null);
  
  // Fetch user's mailing address on mount
  useEffect(() => {
    const fetchMailingAddress = async () => {
      try {
        const response = await fetch(`${API}/guest/status?email=${encodeURIComponent(userEmail)}`);
        const data = await response.json();
        if (data.mailing_address) {
          setMailingAddress(data.mailing_address);
        }
      } catch (err) {
        console.error('Error fetching mailing address:', err);
      }
    };
    if (userEmail && isOpen) {
      fetchMailingAddress();
    }
  }, [userEmail, isOpen]);
  
  // Handle "Same as mailing" checkbox
  const handleSameAsMailing = (checked) => {
    setSameAsMailing(checked);
    if (checked && mailingAddress) {
      setBillingStreet(mailingAddress.street || '');
      setBillingCity(mailingAddress.city || '');
      setBillingState(mailingAddress.state || '');
      setBillingZip(mailingAddress.zip_code || '');
      setBillingCountry(mailingAddress.country || '');
    } else if (!checked) {
      setBillingStreet('');
      setBillingCity('');
      setBillingState('');
      setBillingZip('');
      setBillingCountry('');
    }
  };

  // Prevent closing if forcePayment is enabled or new signup
  const handleClose = () => {
    if (forcePayment || isNewSignup) {
      toast.error('Please complete payment and upload proof to continue');
      return;
    }
    onClose();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        toast.error('Only PNG, JPG, JPEG files are allowed');
        return;
      }
      setScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaymentSubmitted = () => {
    if (!acceptedRefundPolicy) {
      toast.error('Please accept the No Refund Policy to continue');
      return;
    }
    setShowUploadForm(true);
  };

  const handleSubmitProof = async () => {
    if (!payerName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!screenshot) {
      toast.error('Please upload a payment screenshot');
      return;
    }

    setUploading(true);

    try {
      // Save billing address first
      if (billingStreet && billingCity) {
        await fetch(`${API}/guest/update-billing-address`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userEmail,
            billing_street: billingStreet,
            billing_city: billingCity,
            billing_state: billingState,
            billing_zip: billingZip,
            billing_country: billingCountry,
            same_as_mailing: sameAsMailing
          })
        });
      }
      
      const reader = new FileReader();
      reader.readAsDataURL(screenshot);
      reader.onload = async () => {
        const base64Data = reader.result;
        
        const response = await fetch(`${API}/payment-proof/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userEmail,
            payer_name: payerName.trim(),
            screenshot: base64Data,
            filename: screenshot.name,
            plan: selectedPlan,
            offer_code: PLANS[selectedPlan].offerCodeEligible ? offerCode : ''
          })
        });

        const data = await response.json();

        if (data.success) {
          setProofSubmitted(true);
          toast.success('Payment proof submitted successfully!');
        } else {
          toast.error(data.message || 'Failed to upload payment proof');
        }
        setUploading(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
    } catch (error) {
      toast.error('Failed to upload payment proof');
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  // Show success message after proof submitted
  if (proofSubmitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="relative w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10">
          <div className="p-6 text-center space-y-4">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Payment Proof Submitted!</h2>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-300 text-sm">
                ⏰ <strong>Approval may take up to 6 hours, sometimes instantly.</strong>
              </p>
              <p className="text-gray-400 text-xs mt-2">
                You will receive access once your payment is verified by our admin team.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4 text-left">
              <p className="text-gray-300 text-sm mb-2">You can also contact our billing team via WhatsApp:</p>
              <a 
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-3 transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Contact on WhatsApp
              </a>
            </div>
            
            <p className="text-gray-500 text-xs">
              Check your email for updates on your subscription status.
            </p>
            
            <Button
              onClick={() => {
                setProofSubmitted(false);
                onClose();
              }}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show upload form after clicking "I've Made Payment"
  if (showUploadForm) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="relative w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10 max-h-[95vh] overflow-y-auto">
          {!forcePayment && !isNewSignup && (
            <button 
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          )}
          
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
            <h2 className="text-xl font-bold text-white text-center">Upload Payment Proof</h2>
            <p className="text-green-100 text-sm text-center mt-1">
              {PLANS[selectedPlan].name} - {PLANS[selectedPlan].price}/{PLANS[selectedPlan].duration}
            </p>
          </div>

          <div className="p-6 space-y-4">
            {/* Two ways to submit */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-300 text-sm font-medium mb-2">📤 Two ways to submit payment proof:</p>
              <ol className="text-gray-300 text-xs space-y-1 ml-4 list-decimal">
                <li>Upload here (below)</li>
                <li>Send via WhatsApp to our billing team</li>
              </ol>
            </div>

            {/* WhatsApp Option */}
            <a 
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-3 transition-colors w-full"
            >
              <MessageCircle className="w-5 h-5" />
              Send Proof via WhatsApp
            </a>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-800 text-gray-400">OR upload here</span>
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label className="text-sm font-medium text-gray-200 mb-2 block">Your Name (as used during signup)</label>
              <Input
                type="text"
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
                placeholder="Enter your full name"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
            </div>

            {/* Email Display */}
            <div>
              <label className="text-sm font-medium text-gray-200 mb-2 block">Email Address</label>
              <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-gray-300 text-sm">
                {userEmail}
              </div>
              <p className="text-xs text-gray-500 mt-1">Include this email in your payment screenshot</p>
            </div>

            {/* Screenshot Upload */}
            <div>
              <label className="text-sm font-medium text-gray-200 mb-2 block">Payment Screenshot</label>
              <div 
                className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center hover:border-white/40 transition-colors cursor-pointer"
                onClick={() => document.getElementById('screenshot-input').click()}
              >
                {screenshotPreview ? (
                  <div className="space-y-2">
                    <img src={screenshotPreview} alt="Preview" className="max-h-40 mx-auto rounded-lg" />
                    <p className="text-green-400 text-sm">✓ Image selected</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                    <p className="text-gray-400 text-sm">Click to upload screenshot</p>
                    <p className="text-gray-500 text-xs">PNG, JPG up to 5MB</p>
                  </div>
                )}
                <input
                  id="screenshot-input"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-yellow-300 text-xs">
                ⚠️ <strong>Important:</strong> Your screenshot must include:
              </p>
              <ul className="text-gray-300 text-xs mt-1 ml-4 list-disc">
                <li>The name on which account was created</li>
                <li>Your email address: <strong>{userEmail}</strong></li>
              </ul>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmitProof}
              disabled={uploading || !payerName.trim() || !screenshot}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-4"
            >
              {uploading ? 'Uploading...' : 'Submit Payment Proof'}
            </Button>

            <p className="text-gray-500 text-xs text-center">
              Approval may take up to 6 hours, sometimes instantly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main payment modal with plan selection
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10 max-h-[95vh] overflow-y-auto">
        {!forcePayment && !isNewSignup && (
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        )}

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Crown className="w-6 h-6 text-yellow-300" />
            <h2 className="text-xl font-bold text-white">Choose Your Option</h2>
          </div>
          <p className="text-purple-100 text-sm text-center">
            {isNewSignup ? 'Complete payment to activate your account' : 'Subscribe or get 1-on-1 coaching'}
          </p>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Mode Toggle: Subscription vs Coaching */}
          <div className="flex rounded-xl bg-white/5 p-1 mb-4">
            <button
              onClick={() => setPaymentMode('subscription')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                paymentMode === 'subscription'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              Subscribe to Bot
            </button>
            <button
              onClick={() => setPaymentMode('coaching')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                paymentMode === 'coaching'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <User className="w-4 h-4" />
              1-on-1 Coaching
            </button>
          </div>

          {/* SUBSCRIPTION MODE */}
          {paymentMode === 'subscription' && (
            <>
          {/* Plan Selection */}
          <div className="grid grid-cols-2 gap-3">
            {/* Essential Plan */}
            <div 
              onClick={() => setSelectedPlan('essential')}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedPlan === 'essential' 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : 'border-white/20 hover:border-white/40'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-blue-400" />
                <span className="font-bold text-white text-sm">Essential</span>
              </div>
              <div className="text-2xl font-bold text-white">$20</div>
              <div className="text-gray-400 text-xs">5 Days Access</div>
              <ul className="mt-2 space-y-1">
                <li className="text-gray-300 text-xs flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-400" /> Signal Generation
                </li>
                <li className="text-gray-300 text-xs flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-400" /> Trade Tracking
                </li>
              </ul>
              {selectedPlan === 'essential' && (
                <div className="mt-2 text-blue-400 text-xs font-medium">✓ Selected</div>
              )}
            </div>

            {/* Premium Plan */}
            <div 
              onClick={() => setSelectedPlan('premium')}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${
                selectedPlan === 'premium' 
                  ? 'border-purple-500 bg-purple-500/10' 
                  : 'border-white/20 hover:border-white/40'
              }`}
            >
              <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                BEST VALUE
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-purple-400" />
                <span className="font-bold text-white text-sm">Premium</span>
              </div>
              <div className="text-2xl font-bold text-white">$59.99</div>
              <div className="text-gray-400 text-xs">1 Month Access</div>
              <ul className="mt-2 space-y-1">
                <li className="text-gray-300 text-xs flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-400" /> All Essential Features
                </li>
                <li className="text-gray-300 text-xs flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-400" /> Offer Codes Eligible
                </li>
                <li className="text-gray-300 text-xs flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-400" /> Priority Support
                </li>
              </ul>
              {selectedPlan === 'premium' && (
                <div className="mt-2 text-purple-400 text-xs font-medium">✓ Selected</div>
              )}
            </div>
          </div>

          {/* Offer Code - Only for Premium */}
          {selectedPlan === 'premium' && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
              <label className="text-sm font-medium text-purple-300 mb-2 block">
                🎁 Have an offer code?
              </label>
              <Input
                type="text"
                value={offerCode}
                onChange={(e) => setOfferCode(e.target.value.toUpperCase())}
                placeholder="Enter offer code"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
            </div>
          )}

          {selectedPlan === 'essential' && (
            <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-3">
              <p className="text-gray-400 text-xs text-center">
                ℹ️ Essential plan does not qualify for offer codes. Upgrade to Premium to use offer codes.
              </p>
            </div>
          )}

          {/* Billing Address Section */}
          {!paymentMethod && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-3">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Billing Address
              </h4>
              
              {mailingAddress && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameAsMailing}
                    onChange={(e) => handleSameAsMailing(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500"
                  />
                  <span className="text-sm text-gray-300">Same as mailing address</span>
                </label>
              )}
              
              <div className="space-y-2">
                <Input
                  type="text"
                  value={billingStreet}
                  onChange={(e) => setBillingStreet(e.target.value)}
                  placeholder="Street Address"
                  disabled={sameAsMailing}
                  className={`bg-white/10 border-white/20 text-white placeholder:text-gray-500 ${sameAsMailing ? 'opacity-60' : ''}`}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="text"
                    value={billingCity}
                    onChange={(e) => setBillingCity(e.target.value)}
                    placeholder="City"
                    disabled={sameAsMailing}
                    className={`bg-white/10 border-white/20 text-white placeholder:text-gray-500 ${sameAsMailing ? 'opacity-60' : ''}`}
                  />
                  <Input
                    type="text"
                    value={billingState}
                    onChange={(e) => setBillingState(e.target.value)}
                    placeholder="State/Province"
                    disabled={sameAsMailing}
                    className={`bg-white/10 border-white/20 text-white placeholder:text-gray-500 ${sameAsMailing ? 'opacity-60' : ''}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="text"
                    value={billingZip}
                    onChange={(e) => setBillingZip(e.target.value)}
                    placeholder="ZIP/Postal Code"
                    disabled={sameAsMailing}
                    className={`bg-white/10 border-white/20 text-white placeholder:text-gray-500 ${sameAsMailing ? 'opacity-60' : ''}`}
                  />
                  <Input
                    type="text"
                    value={billingCountry}
                    onChange={(e) => setBillingCountry(e.target.value)}
                    placeholder="Country"
                    disabled={sameAsMailing}
                    className={`bg-white/10 border-white/20 text-white placeholder:text-gray-500 ${sameAsMailing ? 'opacity-60' : ''}`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Selection - Crypto Only */}
          {!paymentMethod && billingStreet && billingCity && billingState && billingZip && (
            <div className="space-y-4">
              <h3 className="text-white font-medium text-center">Payment Method</h3>
              
              {/* Crypto Option - Manual Verification */}
              <button
                onClick={() => setPaymentMethod('crypto')}
                className="w-full p-4 rounded-xl border-2 border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-orange-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-bold">Cryptocurrency</p>
                      <p className="text-orange-400 text-xs">USDT on BNB Smart Chain</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                      SECURE
                    </span>
                    <p className="text-orange-300 text-xs mt-1">Up to 6h review</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* PayLink Payment Flow */}
          {/* Crypto Payment Flow */}
          {paymentMethod === 'crypto' && !showUploadForm && (
            <>
              <div className="mb-2">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-orange-400" /> Crypto Payment
                </h3>
              </div>

              {/* Payment Details */}
              <div className="bg-white/5 rounded-xl p-4 space-y-3">
                {/* QR Code */}
                <div className="flex justify-center">
                  <img src={QR_CODE_URL} alt="Payment QR" className="w-32 h-32 rounded-lg" />
                </div>

                {/* Network & Token */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 text-center">
                  <p className="text-yellow-300 text-xs font-medium">
                    Network: {PAYMENT_NETWORK} | Token: {PAYMENT_TOKEN}
                  </p>
                </div>

                {/* Amount */}
                <div className="text-center">
                  <p className="text-gray-400 text-sm">Amount to pay:</p>
                  <p className="text-2xl font-bold text-white">{PLANS[selectedPlan].price} USDT</p>
                </div>
              </div>

              {/* No Refund Policy Section */}
              <div className="border border-white/20 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowRefundPolicy(!showRefundPolicy)}
                  className="w-full p-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="text-white font-medium text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4 text-red-400" />
                    No Refund Policy
                  </span>
                  {showRefundPolicy ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                {showRefundPolicy && (
                  <div className="p-3 border-t border-white/10 bg-black/20">
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap max-h-40 overflow-y-auto font-sans">
                      {NO_REFUND_POLICY}
                    </pre>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedRefundPolicy}
                  onChange={(e) => setAcceptedRefundPolicy(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-red-500/50 text-red-500 focus:ring-red-500"
                />
                <span className="text-sm text-red-300">
                  I have read and accept the <strong>No Refund Policy</strong>. I understand all sales are final.
                </span>
              </label>

              {/* Payment Options */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handlePaymentSubmitted}
                  disabled={!acceptedRefundPolicy}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-bold py-3 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Proof
                </Button>
                
                <Button
                  onClick={() => window.open(WHATSAPP_LINK, '_blank')}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
              
              <p className="text-gray-400 text-xs text-center">
                Send payment proof via WhatsApp with your email: {userEmail}
              </p>
            </>
          )}

          {/* Upload Form - for crypto payments */}
          {paymentMethod === 'crypto' && showUploadForm && !proofSubmitted && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">Upload Payment Proof</h3>
                <button
                  onClick={() => setShowUploadForm(false)}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  ← Back
                </button>
              </div>
              
              <Input
                type="text"
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
                placeholder="Your name (as shown in payment)"
                className="bg-white/10 border-white/20 text-white"
              />
              
              <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center">
                {screenshotPreview ? (
                  <div className="space-y-2">
                    <img src={screenshotPreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setScreenshot(null);
                        setScreenshotPreview(null);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-400 text-sm">Click to upload screenshot</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              
              <Button
                onClick={handleSubmitProof}
                disabled={uploading || !payerName || !screenshot}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {uploading ? 'Submitting...' : 'Submit Payment Proof'}
              </Button>
            </div>
          )}

          {/* Success Message */}
          {proofSubmitted && paymentMode === 'subscription' && (
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-white font-bold text-lg">Payment Submitted!</h3>
              <p className="text-gray-400 text-sm">
                Your payment is being reviewed. Approval may take up to 6 hours.
              </p>
            </div>
          )}

          {userEmail && !proofSubmitted && paymentMode === 'subscription' && (
            <p className="text-[10px] sm:text-xs text-center text-gray-500">
              Payment for: {userEmail}
            </p>
          )}
            </>
          )}

          {/* COACHING MODE */}
          {paymentMode === 'coaching' && (
            <div className="space-y-4">
              {/* Coaching Package Selection */}
              <div className="space-y-3">
                {/* 1 Hour Package */}
                <div 
                  onClick={() => setSelectedCoachingPkg('1hr')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedCoachingPkg === '1hr' 
                      ? 'border-purple-500 bg-purple-500/10' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                        <span className="text-lg">⏰</span>
                      </div>
                      <div>
                        <span className="font-bold text-white">1-Hour Session</span>
                        <p className="text-xs text-gray-400">Comprehensive coaching</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold text-green-400">$100</span>
                      {selectedCoachingPkg === '1hr' && (
                        <p className="text-xs text-purple-400">✓ Selected</p>
                      )}
                    </div>
                  </div>
                  <ul className="text-xs text-gray-400 space-y-1 mt-2">
                    <li className="flex items-start gap-1"><CheckCircle2 className="w-3 h-3 text-green-400 mt-0.5" /> Market structure & strategies</li>
                    <li className="flex items-start gap-1"><CheckCircle2 className="w-3 h-3 text-green-400 mt-0.5" /> Key zones & manipulation</li>
                    <li className="flex items-start gap-1"><CheckCircle2 className="w-3 h-3 text-green-400 mt-0.5" /> Q&A session included</li>
                  </ul>
                </div>

                {/* 30 Min Package */}
                <div 
                  onClick={() => setSelectedCoachingPkg('30min')}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedCoachingPkg === '30min' 
                      ? 'border-blue-500 bg-blue-500/10' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
                        <span className="text-lg">⚡</span>
                      </div>
                      <div>
                        <span className="font-bold text-white">30-Minute Session</span>
                        <p className="text-xs text-gray-400">Quick start guide</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold text-green-400">$55</span>
                      {selectedCoachingPkg === '30min' && (
                        <p className="text-xs text-blue-400">✓ Selected</p>
                      )}
                    </div>
                  </div>
                  <ul className="text-xs text-gray-400 space-y-1 mt-2">
                    <li className="flex items-start gap-1"><CheckCircle2 className="w-3 h-3 text-blue-400 mt-0.5" /> Trading tool walkthrough</li>
                    <li className="flex items-start gap-1"><CheckCircle2 className="w-3 h-3 text-blue-400 mt-0.5" /> Market structure basics</li>
                  </ul>
                </div>

                {/* Custom Package */}
                <div 
                  onClick={() => setSelectedCoachingPkg('custom')}
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedCoachingPkg === 'custom' 
                      ? 'border-yellow-500 bg-yellow-500/10' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🎯</span>
                      <span className="font-bold text-white">Custom Package</span>
                    </div>
                    <span className="text-yellow-400 text-sm">Contact for pricing</span>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-xs text-yellow-300 text-center">
                  ⚠️ Educational content only. Not financial advice.
                </p>
              </div>

              {/* Cancellation Policy Notice */}
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs text-red-300 font-medium mb-1">⚠️ Cancellation Policy</p>
                <p className="text-xs text-gray-400">
                  <strong className="text-red-300">50% cancellation fee</strong> for cancellations with less than 1 hour notice or no-show. You will be refunded half of your payment.
                </p>
              </div>

              {/* Payment for Coaching */}
              {selectedCoachingPkg && selectedCoachingPkg !== 'custom' && (
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-xl">
                    <h4 className="text-white font-medium text-center mb-3">
                      Pay {COACHING_PACKAGES[selectedCoachingPkg]?.price} for {COACHING_PACKAGES[selectedCoachingPkg]?.name}
                    </h4>
                    
                    {/* QR Code */}
                    <div className="flex justify-center mb-3">
                      <img src={QR_CODE_URL} alt="Payment QR" className="w-28 h-28 rounded-lg" />
                    </div>

                    {/* Wallet Address */}
                    <div className="p-2 bg-black/40 rounded-lg mb-3">
                      <p className="text-xs text-gray-500 mb-1 text-center">Send USDT (BEP-20) to:</p>
                      <div className="flex items-center gap-2 justify-center">
                        <code className="text-xs text-cyan-400 break-all">{WALLET_ADDRESS}</code>
                        <button
                          onClick={() => copyToClipboard(WALLET_ADDRESS)}
                          className="p-1 hover:bg-white/10 rounded"
                        >
                          <Copy className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                    </div>

                    <p className="text-xs text-gray-400 text-center mb-3">
                      After payment, contact us on WhatsApp with your email and preferred date/time.
                    </p>

                    <Button
                      onClick={() => window.open(WHATSAPP_LINK, '_blank')}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contact on WhatsApp to Book
                    </Button>
                  </div>
                </div>
              )}

              {/* Custom Package Message */}
              {selectedCoachingPkg === 'custom' && (
                <div className="p-4 bg-white/5 rounded-xl text-center">
                  <p className="text-gray-300 mb-3">Contact us to discuss your custom coaching needs</p>
                  <Button
                    onClick={() => window.open(WHATSAPP_LINK, '_blank')}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Contact on WhatsApp
                  </Button>
                </div>
              )}

              {userEmail && (
                <p className="text-[10px] sm:text-xs text-center text-gray-500">
                  Contact email: {userEmail}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
